import * as yup from 'yup';
import { useFormik } from 'formik';
import { useCallback } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { Card, CardContent, Grid } from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import { regexYear, regexOnlyNumber } from 'shell/constants/regex';
import { Typography } from '@mui/material';

export interface NumeratorForm {
  numeratorRegisterType?: string;
  year?: string;
  description?: string;
  currentNumerator?: string;
}

interface FormDetailProps {
  onOk: (form?: NumeratorForm) => void;
  initialValues?: NumeratorForm;
  onCancel: () => void;
  disabled?: boolean;
  title?: string;
}
const validationSchema = yup.object({
  year: yup.string().matches(regexYear, 'Il campo deve rappresentare un anno {YYYY}'),
  currentNumerator: yup.string().matches(regexOnlyNumber, "Il campo dev'essere numerico"),
  description: yup.string().max(50, 'Il campo descrizione non può superare i 50 caratteri'),
});

const Detail = ({
  onOk,
  initialValues,
  onCancel,
  disabled,
  title,
}: FormDetailProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const numeratorForm = useFormik<NumeratorForm>({
    initialValues: initialValues || {
      numeratorRegisterType: '',
      year: '',
      description: '',
      currentNumerator: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={numeratorForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  error={
                    numeratorForm.touched.numeratorRegisterType &&
                    Boolean(numeratorForm.errors.numeratorRegisterType)
                  }
                  helperText={numeratorForm.errors.numeratorRegisterType}
                  label="Tipo registro *"
                  id="numeratorRegisterType"
                  name="numeratorRegisterType"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={numeratorForm.values.numeratorRegisterType || ''}
                  onChange={(e) => {
                    numeratorForm.setFieldValue('numeratorRegisterType', e.target.value);
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  error={
                    numeratorForm.touched.description && Boolean(numeratorForm.errors.description)
                  }
                  helperText={numeratorForm.errors.description}
                  label="Descrizione numeratore"
                  id="description"
                  name="description"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={numeratorForm.values.description || ''}
                  onChange={(e) => {
                    numeratorForm.setFieldValue('description', e.target.value);
                  }}
                  inputProps={{ maxLength: '50' }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  error={numeratorForm.touched.year && Boolean(numeratorForm.errors.year)}
                  helperText={numeratorForm.errors.year}
                  label="Anno documento"
                  id="year"
                  name="year"
                  fullWidth
                  inputProps={{ maxLength: '4' }}
                  type="text"
                  variant="outlined"
                  value={numeratorForm.values.year || ''}
                  onChange={(e) => {
                    numeratorForm.setFieldValue('year', e.target.value);
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  error={
                    numeratorForm.touched.currentNumerator &&
                    Boolean(numeratorForm.errors.currentNumerator)
                  }
                  helperText={numeratorForm.errors.currentNumerator}
                  label="Numeratore attuale"
                  id="currentNumerator"
                  name="currentNumerator"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={numeratorForm.values.currentNumerator || ''}
                  onChange={(e) => {
                    numeratorForm.setFieldValue('currentNumerator', e.target.value);
                  }}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </div>
          <div style={{ padding: '24px 0 16px 0' }}>
            <div className="pmi-mr-3 pmi-d-inline-block">
              <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
                Conferma
              </PmiButton>
            </div>
            <PmiButton
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                onCancel();
              }}
            >
              Indietro
            </PmiButton>
          </div>
        </CardContent>
      </form>
    </Card>
  );
};
Detail.displayName = 'Numerator detail component';

export default Detail;
